import { useState } from 'react'
import { CalendarPlus } from 'lucide-react'
import { Button, FormError } from '../../core/index.js'
import { schedulingRulesOf, useFacilityConfig } from '../../facilities/index.js'
import { useAvailableShifts, usePublishShifts } from '../hooks.js'
import { missingSeatInputs, publishBatches, type MissingSeats } from './publishShiftsData.js'
import { todayDate } from './scheduleCalendarData.js'

/**
 * Publishes the seats a Month card finding says were never published, in
 * place: the unit, shift type, role and dates it names, at the facility's
 * hours for the shift type, as many on each date as are missing. The month's
 * health is read again after, so the finding goes once the seats exist.
 */
export function PublishMissingSeats({ facilityId, missing }: { facilityId: string; missing: MissingSeats }) {
  const config = useFacilityConfig(facilityId)
  const shifts = useAvailableShifts(facilityId)
  const publish = usePublishShifts(facilityId)
  const [written, setWritten] = useState<number | null>(null)
  const [error, setError] = useState<unknown>(null)
  const inputs = config.data && shifts.data !== undefined ? missingSeatInputs(missing, schedulingRulesOf(config.data), shifts.data ?? [], todayDate()) : null
  const seats = Object.entries(missing.seatsByDate).filter(([date]) => date >= todayDate()).reduce((sum, [, count]) => sum + count, 0)

  async function run(): Promise<void> {
    if (!inputs || inputs.length === 0) {
      return
    }
    setError(null)
    setWritten(null)
    try {
      let total = 0
      // In parts, as the Publish shifts form sends them, so a large month stays within the gateway's time.
      for (const part of publishBatches(inputs)) {
        total += (await publish.mutateAsync(part)).length
      }
      setWritten(total)
    } catch (caught: unknown) {
      setError(caught)
    }
  }

  if (inputs !== null && inputs.length === 0) {
    return <span className="text-[10px] text-ink-mid">Its dates have passed, or the facility does not offer this shift type.</span>
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <Button icon={CalendarPlus} size="sm" disabled={inputs === null || publish.isPending} onClick={() => void run()}>
        {publish.isPending ? 'Publishing…' : `Publish ${seats === 1 ? 'the seat' : `these ${seats} seats`}`}
      </Button>
      {written !== null && <span className="text-[10px] font-bold text-green">Published {written} {written === 1 ? 'seat' : 'seats'}. Build the month again to fill them.</span>}
      {error !== null && <FormError error={error} fallback="The seats could not be published. Publishing them again is safe." />}
    </div>
  )
}
